import React, { useContext, useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { i18n } from "../../translate/i18n";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import ConfirmationModal from "../../components/ConfirmationModal";
import GclickModal from "../../components/Platforms/GclickModal";

import gclickLogo from "../../assets/gclick.webp";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  subtitle: {
    marginTop: -theme.spacing(1),
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(0.5),
  },
  tableWrap: {
    ...theme.scrollbarStyles,
    overflowX: "auto",
  },
  catalogHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    flexWrap: "wrap",
  },
  cardRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  platformCard: {
    width: 240,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    cursor: "pointer",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
  },
  platformCardDisabled: {
    cursor: "not-allowed",
    pointerEvents: "none",
    opacity: 0.5,
    filter: "grayscale(0.4)",
  },
  logo: {
    width: 44,
    height: 44,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    backgroundColor: theme.mode === "light" ? "#e9eefc" : "#232c4a",
    color: theme.palette.primary.main,
    overflow: "hidden",
  },
  logoImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  integrationName: {
    color: theme.palette.text.secondary,
  },
  // Exclusão em massa é destrutiva, não um "cancelar" -- contained e
  // vermelho. O Button do MUI v4 não tem color="error", daí vir pelo tema.
  bulkRow: {
    marginBottom: theme.spacing(0.5),
  },
  bulkMessage: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  bulkDeleteBtn: {
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  platformName: {
    fontWeight: 700,
    fontSize: "0.92rem",
  },
  platformUrl: {
    fontSize: "0.74rem",
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
  },
  pageWrap: {
    ...theme.scrollbarStyles,
    flex: 1,
    overflowY: "scroll",
    padding: theme.spacing(2),
  },
}));

const PAGE_LIMIT = 10;

// Endpoint de exclusão por integração. Fica no frontend de propósito: o
// catálogo do backend não manda URL pro cliente sair chamando com DELETE.
//
// A chave é o rowId INTEIRO ("plataforma:integração"), não só a integração.
// Com a chave pela metade, uma segunda plataforma que também chamasse alguma
// integração de "app" cairia no endpoint do G-Click e apagaria a credencial
// dele -- em silêncio, com toast de sucesso. Chaveando pelo par, plataforma
// desconhecida não acha nada e falha na cara do usuário.
const DELETE_ENDPOINTS = {
  "gclick:aiAgent": "/gclickSettings/ai-agent",
  "gclick:app": "/gclickSettings/app",
};

// Texto da confirmação é genérico de propósito: não fala de plataforma nem
// de integração específica. Aviso escrito à mão por integração (a) precisava
// ser chaveado certo em mais um lugar, (b) era texto só do G-Click que
// apareceria embaixo da linha de qualquer outra plataforma, e (c) na exclusão
// em massa virava um parágrafo por linha selecionada -- parede de texto que
// ninguém lê, o que piora a confirmação em vez de melhorar.
//
// Nada do que se apaga aqui é irreversível: colou a credencial de novo e
// volta a funcionar. Conversa do Agente de I.A. não é mais apagada (ver
// deleteAiAgentConfig no backend).

// Um logo real por logoId; qualquer plataforma sem entrada aqui cai de
// volta pras iniciais -- não precisa de imagem pra aparecer no catálogo.
const LOGOS = {
  gclick: gclickLogo,
};

const logoInitials = name => name.slice(0, 2).toUpperCase();

const PlatformLogo = ({ platform, className }) => {
  const src = LOGOS[platform.logoId];
  return src ? (
    <img src={src} alt={platform.name} className={className} />
  ) : (
    logoInitials(platform.name)
  );
};

const Platforms = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [catalogPage, setCatalogPage] = useState(1);
  const [tablePage, setTablePage] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [gclickStatus, setGclickStatus] = useState({
    enabled: false,
    hasAiAgentConfig: false,
    integrations: {},
  });
  const [modalPlatformId, setModalPlatformId] = useState(null);
  const [modalInitialTab, setModalInitialTab] = useState(0);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");

  // Re-busca só o status (o catálogo é estático). Chamado ao fechar o modal e
  // depois de cada exclusão: o "está configurada?" de cada integração vem do
  // servidor, então patch otimista no cliente não serve como fonte da tabela.
  const refreshStatus = async () => {
    try {
      const { data } = await api.get("/gclickSettings/status");
      setGclickStatus(data);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catalogRes, statusRes] = await Promise.all([
          api.get("/platforms/catalog"),
          api.get("/gclickSettings/status")
        ]);
        setCatalog(catalogRes.data);
        setGclickStatus(statusRes.data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setCatalogPage(1);
  }, [search]);

  useEffect(() => {
    setTablePage(1);
  }, [tableSearch]);

  // Só o G-Click tem status real hoje -- ver PLANO_PLATAFORMAS_GCLICK.md.
  // Plataformas sem integração real (hasRealIntegration: false) nunca têm
  // status próprio, não faz sentido buscar nada pra elas.
  const statusFor = platformId => (platformId === "gclick" ? gclickStatus : null);

  const deleteRow = rowId => {
    const endpoint = DELETE_ENDPOINTS[rowId];
    if (!endpoint) {
      // String e não Error: o toastError só sabe ler erro do axios ou string
      // solta -- com um Error ele cai no "An error occurred!" genérico, que
      // não ajuda ninguém a descobrir que falta entrada no DELETE_ENDPOINTS.
      return Promise.reject(`Sem endpoint de exclusão para "${rowId}"`);
    }
    return api.delete(endpoint);
  };

  // `enabled` nunca é tocado na exclusão: é a liberação do boss, não some
  // porque a empresa apagou as próprias credenciais.
  const handleDeleteRow = async () => {
    const rowId = deletingRowId;
    setDeletingRowId(null);
    try {
      await deleteRow(rowId);
      setSelectedRows(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      await refreshStatus();
      toast.success(i18n.t("platforms.table.delete.toastSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  // allSettled em vez de Promise.all: se uma exclusão falhar, as outras já
  // aconteceram -- o usuário precisa saber quantas deram certo, e as que
  // falharam continuam selecionadas pra tentar de novo. Mesmo padrão da
  // página de Fluxos.
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedRows);
    closeBulkConfirm();

    const results = await Promise.allSettled(ids.map(deleteRow));
    const failedIds = ids.filter((id, index) => results[index].status === "rejected");
    const deletedCount = ids.length - failedIds.length;

    setSelectedRows(new Set(failedIds));
    await refreshStatus();

    if (failedIds.length === 0) {
      toast.success(
        i18n.t("platforms.table.bulkDelete.toastSuccess", { count: deletedCount })
      );
    } else if (deletedCount > 0) {
      toast.warn(
        i18n.t("platforms.table.bulkDelete.toastPartial", {
          deleted: deletedCount,
          failed: failedIds.length,
        })
      );
    } else {
      toastError(results[0].reason);
    }
  };

  // Uma linha por INTEGRAÇÃO configurada, não por plataforma: cada aba do
  // modal é uma integração independente. Ver PLANO_TABELA_INTEGRACOES.md.
  // Tanto a lista de integrações quanto o "está configurada?" vêm do backend
  // (PLATFORMS_CATALOG[].integrations e /gclickSettings/status -> integrations),
  // então nada aqui precisa saber o que é G-Click.
  const integrationRows = catalog
    .filter(p => p.hasRealIntegration)
    .flatMap(p => {
      const status = statusFor(p.id);
      return (p.integrations || []).map((intg, tabIndex) => ({
        rowId: `${p.id}:${intg.id}`,
        platform: p,
        label: i18n.t(intg.nameKey),
        // Índice da aba no modal = posição no catálogo (ver GclickModal).
        tabIndex,
        liberado: !!status?.enabled,
        configured: !!status?.integrations?.[intg.id],
      }));
    })
    .filter(r => r.configured);

  const labelFor = rowId => {
    const row = integrationRows.find(r => r.rowId === rowId);
    return row ? `${row.platform.name} · ${row.label}` : rowId;
  };

  const filteredCatalog = catalog.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );
  const catalogTotalPages = Math.max(1, Math.ceil(filteredCatalog.length / PAGE_LIMIT));
  const pagedCatalog = filteredCatalog.slice(
    (catalogPage - 1) * PAGE_LIMIT,
    catalogPage * PAGE_LIMIT
  );

  // Busca casa nome da plataforma E rótulo da integração -- "aplicativo" tem
  // que achar a linha, não só "omie".
  const filteredRows = integrationRows.filter(r =>
    `${r.platform.name} ${r.label}`.toLowerCase().includes(tableSearch.toLowerCase())
  );
  const tableTotalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_LIMIT));
  const pagedRows = filteredRows.slice(
    (tablePage - 1) * PAGE_LIMIT,
    tablePage * PAGE_LIMIT
  );

  // Só linha liberada entra na seleção: toda rota de exclusão exige a
  // liberação do boss (isGclickEnabled) e devolveria 403. O master considera
  // apenas as linhas visíveis E liberadas, então não existe seleção capaz de
  // virar erro.
  const selectableRows = pagedRows.filter(r => r.liberado);
  const selectedVisibleCount = selectableRows.filter(r => selectedRows.has(r.rowId)).length;
  const allVisibleSelected =
    selectableRows.length > 0 && selectedVisibleCount === selectableRows.length;

  const toggleRow = rowId =>
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });

  // Exclusão em massa pede a palavra digitada (pedido do boss; o padrão já
  // existe em CompaniesManager e ContactDeleteConfirmModal). Comparação
  // tolerante -- trim + maiúsculas -- como no CompaniesManager; o
  // ContactDeleteConfirmModal exige igual exato, mas ser exigente com espaço
  // em branco só gera raiva.
  const expectedConfirm = i18n.t("platforms.table.bulkDelete.confirmWord");
  const canConfirmBulk =
    bulkConfirmText.trim().toUpperCase() === expectedConfirm.toUpperCase();

  const openBulkConfirm = () => {
    setBulkConfirmText("");
    setBulkConfirmOpen(true);
  };

  const closeBulkConfirm = () => {
    setBulkConfirmOpen(false);
    setBulkConfirmText("");
  };

  const toggleAllVisible = () =>
    setSelectedRows(prev => {
      const next = new Set(prev);
      selectableRows.forEach(r =>
        allVisibleSelected ? next.delete(r.rowId) : next.add(r.rowId)
      );
      return next;
    });

  const modalPlatform = catalog.find(p => p.id === modalPlatformId);
  const modalStatus = modalPlatform ? statusFor(modalPlatform.id) : null;

  const openModal = (id, tabIndex = 0) => {
    setModalInitialTab(tabIndex);
    setModalPlatformId(id);
  };

  // Re-busca o status ao fechar: configurar algo dentro do modal tem que
  // refletir na tabela sem F5. Os flags por integração vêm do servidor, então
  // o patch otimista do onStatusChange não basta como fonte da tabela.
  const closeModal = () => {
    setModalPlatformId(null);
    refreshStatus();
  };

  // "Conectado" = QUALQUER integração da plataforma configurada, não só o
  // Agente de I.A. -- senão a empresa que configurou apenas o Aplicativo
  // ganha linha na tabela e um card dizendo "Configurar".
  const cardStateFor = platform => {
    if (!platform.hasRealIntegration) return "unavailable";
    const status = statusFor(platform.id);
    // A liberacao do boss vem ANTES de "esta configurada?". Checar
    // anyConfigured primeiro devolvia "unconfigured" -- que e clicavel -- pra
    // empresa sem liberacao E sem configuracao, que e justamente o estado de
    // todo cliente novo: o modal abria, as nove rotas respondiam 403 e o
    // usuario era deslogado. "Bloqueado" tem precedencia.
    if (!status?.enabled) return "disabled";
    const anyConfigured = Object.values(status?.integrations || {}).some(Boolean);
    if (!anyConfigured) return "unconfigured";
    return "connected";
  };

  return (
    <MainContainer>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : loading ? (
        <>
          <MainHeader>
            <Title>Plataformas</Title>
          </MainHeader>
          <Paper className={classes.pageWrap} elevation={1}>
            <Typography>Carregando...</Typography>
          </Paper>
        </>
      ) : (
        <>
          <MainHeader>
            <Title>Plataformas</Title>
          </MainHeader>
          <Paper className={classes.pageWrap} elevation={1}>
          <Typography variant="body2" className={classes.subtitle}>
            A GoLiveZap pode ser integrada com grandes plataformas do mercado mundial
          </Typography>

          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Plataformas Integradas
            </Typography>
            <div className={classes.catalogHeader}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Buscar plataforma..."
                value={tableSearch}
                onChange={e => setTableSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {selectedRows.size > 0 && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("platforms.table.selected", { count: selectedRows.size })}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    className={classes.bulkDeleteBtn}
                    startIcon={<DeleteOutlineIcon />}
                    onClick={openBulkConfirm}
                  >
                    {i18n.t("platforms.table.bulkDelete.button", { count: selectedRows.size })}
                  </Button>
                </>
              )}
            </div>
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                        disabled={selectableRows.length === 0}
                        onChange={toggleAllVisible}
                      />
                    </TableCell>
                    <TableCell>Plataforma</TableCell>
                    <TableCell>Acesso</TableCell>
                    <TableCell>Ação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map(r => (
                    <TableRow key={r.rowId} selected={selectedRows.has(r.rowId)}>
                      {/* Bloqueado também fica fora da seleção, não só sem
                          lápis/lixeira: a exclusão exige a liberação do boss
                          e voltaria 403. */}
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedRows.has(r.rowId)}
                          disabled={!r.liberado}
                          onChange={() => toggleRow(r.rowId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                          <div className={classes.logo}>
                            <PlatformLogo platform={r.platform} className={classes.logoImg} />
                          </div>
                          <span>
                            {r.platform.name}
                            <span className={classes.integrationName}> · {r.label}</span>
                          </span>
                        </Box>
                      </TableCell>
                      {/* Quem libera é o boss (isSuper), não a empresa -- por
                          isso um chip e não um switch. Switch desabilitado
                          pareceria quebrado; chip é informação. É por
                          plataforma, então as duas linhas do G-Click mostram
                          o mesmo valor. */}
                      <TableCell>
                        <Tooltip
                          title={
                            r.liberado
                              ? "Liberado pelo administrador do sistema."
                              : "Bloqueado. Fale com o administrador do sistema para liberar."
                          }
                        >
                          <Chip
                            size="small"
                            label={r.liberado ? "Liberado" : "Bloqueado"}
                            style={
                              r.liberado
                                ? { backgroundColor: "#2e7d32", color: "#fff", fontWeight: 500 }
                                : undefined
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      {/* Bloqueado = sem ação nenhuma. Todas as rotas de
                          /gclickSettings exigem a liberação, então abrir o
                          modal só renderia 403 em toast. O card já é cinza
                          e não clicável pelo mesmo motivo. */}
                      <TableCell>
                        <Tooltip title={r.liberado ? "Editar" : "Bloqueado pelo administrador do sistema."}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!r.liberado}
                              onClick={() => openModal(r.platform.id, r.tabIndex)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={r.liberado ? "Excluir" : "Bloqueado pelo administrador do sistema."}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!r.liberado}
                              onClick={() => setDeletingRowId(r.rowId)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {filteredRows.length > PAGE_LIMIT && (
              <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: 12 }}>
                <Button size="small" disabled={tablePage <= 1} onClick={() => setTablePage(p => p - 1)}>
                  Anterior
                </Button>
                <Typography variant="body2" color="textSecondary">
                  Página {tablePage} de {tableTotalPages}
                </Typography>
                <Button size="small" disabled={tablePage >= tableTotalPages} onClick={() => setTablePage(p => p + 1)}>
                  Próxima
                </Button>
              </Box>
            )}
          </div>

          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Selecione a plataforma desejada
            </Typography>
            <div className={classes.catalogHeader}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Buscar plataforma..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className={classes.cardRow}>
              {pagedCatalog.map(p => {
                const state = cardStateFor(p);
                return (
                  <Paper
                    key={p.id}
                    variant="outlined"
                    className={`${classes.platformCard} ${
                      state === "disabled" ? classes.platformCardDisabled : ""
                    }`}
                    onClick={() => state !== "disabled" && state !== "unavailable" && openModal(p.id)}
                  >
                    <div className={classes.logo}>
                      <PlatformLogo platform={p} className={classes.logoImg} />
                    </div>
                    <div>
                      <div className={classes.platformName}>{p.name}</div>
                      <div className={classes.platformUrl}>{p.url}</div>
                    </div>
                    {state === "connected" && (
                      <Chip
                        size="small"
                        icon={<CheckCircleIcon style={{ color: "#fff" }} />}
                        label="Conectado"
                        style={{ backgroundColor: "#2e7d32", color: "#fff", fontWeight: 500 }}
                      />
                    )}
                    {state === "disabled" && (
                      <Tooltip title="G-Click bloqueado para esta empresa. Fale com o administrador do sistema.">
                        <Chip size="small" label="Bloqueado" />
                      </Tooltip>
                    )}
                    {state === "unconfigured" && (
                      <Chip
                        size="small"
                        label="Configurar"
                        style={{ backgroundColor: "#e9eefc", color: "#3a5fe5", fontWeight: 500 }}
                      />
                    )}
                    {state === "unavailable" && (
                      <Button size="small" variant="contained" color="primary" disabled>
                        Em breve
                      </Button>
                    )}
                  </Paper>
                );
              })}
            </div>
            {filteredCatalog.length > PAGE_LIMIT && (
              <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: 16 }}>
                <Button size="small" disabled={catalogPage <= 1} onClick={() => setCatalogPage(p => p - 1)}>
                  Anterior
                </Button>
                <Typography variant="body2" color="textSecondary">
                  Página {catalogPage} de {catalogTotalPages}
                </Typography>
                <Button size="small" disabled={catalogPage >= catalogTotalPages} onClick={() => setCatalogPage(p => p + 1)}>
                  Próxima
                </Button>
              </Box>
            )}
          </div>
          </Paper>

          <Dialog
            open={!!modalPlatformId}
            onClose={(e, reason) => reason !== "backdropClick" && closeModal()}
            maxWidth="xl"
            fullWidth
          >
            {modalPlatform && (
              <>
                <DialogTitle disableTypography>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{modalPlatform.name}</Typography>
                    <IconButton size="small" onClick={closeModal}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  {modalPlatform.id === "gclick" ? (
                    <GclickModal
                      status={modalStatus}
                      initialTab={modalInitialTab}
                      onStatusChange={patch => setGclickStatus(prev => ({ ...prev, ...patch }))}
                      onClose={closeModal}
                    />
                  ) : (
                    <Box textAlign="center" py={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Essa integração ainda não está pronta
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cada plataforma vai ganhar sua própria tela conforme for sendo
                        construída. Por enquanto só o G-Click tem conteúdo real.
                      </Typography>
                    </Box>
                  )}
                </DialogContent>
              </>
            )}
          </Dialog>

          <ConfirmationModal
            title={i18n.t("platforms.table.delete.dialogTitle")}
            open={!!deletingRowId}
            onClose={() => setDeletingRowId(null)}
            onConfirm={handleDeleteRow}
          >
            {deletingRowId && (
              <>
                <strong>{labelFor(deletingRowId)}</strong>
                {" — "}
                {i18n.t("platforms.table.delete.message")}
              </>
            )}
          </ConfirmationModal>

          {/* Dialog na mão em vez do ConfirmationModal: este precisa da
              palavra digitada, e o ConfirmationModal (usado em meia dúzia de
              telas) não tem isso -- mexer nele por causa daqui mudaria o
              componente de todo mundo. Mesma abordagem do CompaniesManager.
              Os nomes das linhas ficam: vêm do catálogo, não de mapa escrito
              à mão, e respondem "marquei as caixas certas?". */}
          <Dialog open={bulkConfirmOpen} onClose={closeBulkConfirm} maxWidth="xs" fullWidth>
            <DialogTitle>
              {i18n.t("platforms.table.bulkDelete.dialogTitle", {
                count: selectedRows.size,
              })}
            </DialogTitle>
            <DialogContent dividers>
              {/* Corta em 5, igual FlowBuilder/index.js: com a seleção inteira
                  na tela o campo de digitar a palavra ia pro fim de uma lista
                  longa -- com autoFocus nele, o usuário digitava num campo
                  fora da área visível e parecia que nada acontecia. */}
              {Array.from(selectedRows).slice(0, 5).map(rowId => (
                <Typography key={rowId} variant="body2" className={classes.bulkRow}>
                  <strong>{labelFor(rowId)}</strong>
                </Typography>
              ))}
              {selectedRows.size > 5 && (
                <Typography variant="body2" className={classes.bulkRow}>
                  {i18n.t("platforms.table.bulkDelete.andMore", {
                    count: selectedRows.size - 5,
                  })}
                </Typography>
              )}
              <Typography variant="body2" className={classes.bulkMessage}>
                {i18n.t("platforms.table.bulkDelete.dialogMessage")}
              </Typography>
              <TextField
                id="platforms-bulk-confirm"
                size="small"
                variant="outlined"
                fullWidth
                autoFocus
                value={bulkConfirmText}
                onChange={e => setBulkConfirmText(e.target.value)}
                label={i18n.t("platforms.table.bulkDelete.confirmLabel", {
                  word: expectedConfirm,
                })}
              />
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" color="secondary" onClick={closeBulkConfirm}>
                {i18n.t("confirmationModal.buttons.cancel")}
              </Button>
              <Button
                variant="contained"
                className={canConfirmBulk ? classes.bulkDeleteBtn : undefined}
                disabled={!canConfirmBulk}
                onClick={handleBulkDelete}
              >
                {i18n.t("confirmationModal.buttons.confirm")}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </MainContainer>
  );
};

export default Platforms;
